<template>
  <div class="group">
    <div class="mb-4">
      <label for="subject">
        Newsletter Subject
      </label>

      <Input
        :key="saving"
        name="subject"
        type="text"
        :value="campaignSubject"
        :disabled="saving"
        :loading="saving"
        @blur="onSubjectChange($event.target.value)"
      />
    </div>

    <component
      :is="field.__typename"
      v-for="field in campaignFields"
      :key="field.id"
      :saving="saving"
      :setting="field"
      :inline-uploader="true"
      @setting-updated="onSettingUpdated"
      @uploading-complete="onUploadingComplete"
      @uploading-file="saving = true"
    >
      <slot />
    </component>
  </div>
</template>

<script>
import compare from "just-compare";
import SettingTypes from "@/components/Settings/settingTypes";
import SettingsNav from "@/components/Settings/SettingsNav";
import Input from "@/components/UI/Input";
import EventBus from "@/lib/event-bus.js";
import { UpdateCampaignFields } from "@/graphql/mutations";

export default {
  name: "CampaignFields",
  components: { ...SettingTypes, SettingsNav, Input },
  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      campaignSubject: this.campaign?.subject ?? "",
      saving: false,
    };
  },
  computed: {
    campaignFields() {
      return this.campaign?.fields ?? [];
    },
  },
  methods: {
    formatCampaignFields() {
      // Due to the way Interfaces in GraphQL work we need to check for the field named valueField
      // This will give us the property name for the actual value field
      return this.campaignFields
        .filter((field) => {
          // If there is no valueField on the object return false
          if (!this.$has(field, "valueField")) return false;
          // If there is no prop with the value of valueField return false
          if (!this.$has(field, field.valueField)) return false;
          // Otherwise carry on...
          return true;
        })
        .map((field) => {
          return {
            fieldId: field.id,
            value: field[field.valueField],
          };
        });
    },
    isSubjectFieldValid(subject) {
      // Check if there's a subject...
      return subject !== "";
    },
    onSettingUpdated(setting, value) {
      if (this.campaignFields?.length) {
        // Find the setting in the campaign fields based on setting.id...
        const fieldIndex = this.campaignFields.findIndex(
          (field) => field.id === setting.id
        );

        // If the setting has changed then update it and save...
        if (!compare(this.campaignFields[fieldIndex], setting)) {
          // Update the campaign field with the new setting...
          this.campaignFields[fieldIndex] = setting;

          // this.$forceUpdate();
          this.saveCampaignFields();
        }
      }
    },
    onSubjectChange(subject) {
      // Check that the subject field is valid and if it has changed...
      if (
        this.isSubjectFieldValid(subject) &&
        this.campaignSubject !== subject
      ) {
        // then update the subject and save the campaign fields...
        this.campaignSubject = subject;
        this.saveCampaignFields(false);
      } else {
        // Otherwise force an update to put the original subject back...
        this.$forceUpdate();
      }
    },
    onUploadingComplete() {
      this.saveCampaignFields();

      this.$gtm.trackEvent({
        event: "promo-campaign-field-banner-img-uploaded",
      });
    },
    async saveCampaignFields(reloadPreview = true) {
      this.saving = true;

      // Emit the preview loading event to put the Preview component in it's loading state...
      if (reloadPreview) EventBus.$emit("preview-loading", true);

      this.$gtm.trackEvent({
        event: "promo-campaign-fields-save",
        campaignId: this.campaign.campaignId,
      });

      // Send mutation with the campaign fields and subject...
      try {
        await this.$apollo.mutate({
          mutation: UpdateCampaignFields,
          variables: {
            input: {
              campaignId: this.campaign.campaignId,
              subject: this.campaignSubject,
              fields: this.formatCampaignFields(),
            },
          },
          fetchPolicy: "no-cache",
        });

        if (reloadPreview) EventBus.$emit("refetch-preview");

        this.saving = false;
      } catch (error) {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped></style>
